<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import AgentBotFunctionsAPI from 'dashboard/api/agentBotFunctions';
import BotScheduleDay from './BotScheduleDay.vue';
import BotExclusionsList from './BotExclusionsList.vue';
import {
  defaultBotScheduleSlots,
  parseBotSchedule,
  transformBotSchedule,
  botScheduleHasError,
} from '../helpers/botSchedule';

export default {
  components: {
    LoadingState,
    SettingsFieldSection,
    SettingsToggleSection,
    NextButton,
    SelectInput,
    BotScheduleDay,
    BotExclusionsList,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedAgentBotId: null,
      selectedFunctionId: null,
      functions: [],
      isLoadingFunctions: false,
      scheduleEnabled: false,
      scheduleSlots: defaultBotScheduleSlots(),
      effectivelyActive: null,
    };
  },
  computed: {
    dayNames() {
      return {
        0: this.$t('AGENT_BOTS.BOT_CONFIGURATION.SCHEDULE.DAYS.SUNDAY'),
        1: this.$t('AGENT_BOTS.BOT_CONFIGURATION.SCHEDULE.DAYS.MONDAY'),
        2: this.$t('AGENT_BOTS.BOT_CONFIGURATION.SCHEDULE.DAYS.TUESDAY'),
        3: this.$t('AGENT_BOTS.BOT_CONFIGURATION.SCHEDULE.DAYS.WEDNESDAY'),
        4: this.$t('AGENT_BOTS.BOT_CONFIGURATION.SCHEDULE.DAYS.THURSDAY'),
        5: this.$t('AGENT_BOTS.BOT_CONFIGURATION.SCHEDULE.DAYS.FRIDAY'),
        6: this.$t('AGENT_BOTS.BOT_CONFIGURATION.SCHEDULE.DAYS.SATURDAY'),
      };
    },
    ...mapGetters({
      agentBots: 'agentBots/getBots',
      uiFlags: 'agentBots/getUIFlags',
    }),
    currentInboxId() {
      return this.inbox?.id || this.$route.params.inboxId;
    },
    activeAgentBot() {
      return this.$store.getters['agentBots/getActiveAgentBot'](
        this.currentInboxId
      );
    },
    activeFunction() {
      return this.$store.getters['agentBots/getActiveAgentBotFunction'](
        this.currentInboxId
      );
    },
    functionOptions() {
      return this.functions
        .filter(fn => fn.active)
        .map(fn => ({ value: fn.id, label: fn.name }));
    },
    canSubmit() {
      if (!this.selectedAgentBotId || !this.selectedFunctionId) return false;
      if (this.scheduleEnabled && botScheduleHasError(this.scheduleSlots)) {
        return false;
      }
      if (
        this.scheduleEnabled &&
        !this.scheduleSlots.some(slot => slot.enabled)
      ) {
        return false;
      }
      return true;
    },
    timezoneLabel() {
      return this.inbox?.timezone || 'UTC';
    },
  },
  watch: {
    activeAgentBot() {
      this.selectedAgentBotId = this.activeAgentBot?.id || null;
      if (this.selectedAgentBotId) {
        this.loadFunctions();
      }
    },
    activeFunction() {
      this.selectedFunctionId = this.activeFunction?.id || null;
    },
    selectedAgentBotId(newBotId, oldBotId) {
      if (newBotId === oldBotId) return;
      this.selectedFunctionId = null;
      this.loadFunctions();
    },
  },
  mounted() {
    this.fetchBotData();
  },
  methods: {
    async fetchBotData() {
      this.$store.dispatch('agentBots/get');
      const data = await this.$store.dispatch(
        'agentBots/fetchAgentBotInbox',
        this.currentInboxId
      );
      this.applyScheduleFromResponse(data);
    },
    applyScheduleFromResponse(agentBot) {
      if (!agentBot?.id) {
        this.scheduleEnabled = false;
        this.scheduleSlots = defaultBotScheduleSlots();
        this.effectivelyActive = null;
        return;
      }
      this.scheduleEnabled = Boolean(agentBot.schedule_enabled);
      this.scheduleSlots = this.scheduleEnabled
        ? parseBotSchedule(agentBot.schedule)
        : defaultBotScheduleSlots();
      this.effectivelyActive =
        typeof agentBot.effectively_active === 'boolean'
          ? agentBot.effectively_active
          : null;
    },
    async loadFunctions() {
      const botId = this.selectedAgentBotId;
      if (!botId) {
        this.functions = [];
        return;
      }
      this.isLoadingFunctions = true;
      try {
        const { data } = await AgentBotFunctionsAPI.getFunctions(botId);
        this.functions = data || [];
        if (
          this.selectedFunctionId &&
          !this.functions.some(fn => fn.id === this.selectedFunctionId)
        ) {
          this.selectedFunctionId = null;
        }
        if (!this.selectedFunctionId && this.functions.length) {
          const activeFn = this.functions.find(fn => fn.active);
          if (activeFn) this.selectedFunctionId = activeFn.id;
        }
      } catch (_e) {
        this.functions = [];
      } finally {
        this.isLoadingFunctions = false;
      }
    },
    onScheduleDayUpdate(slot) {
      this.scheduleSlots = this.scheduleSlots.map(item =>
        item.day === slot.day ? { ...slot } : item
      );
    },
    async updateActiveAgentBot() {
      if (!this.canSubmit) {
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.FUNCTION_REQUIRED'));
        return;
      }
      try {
        await this.$store.dispatch('agentBots/setAgentBotInbox', {
          inboxId: this.inbox.id,
          botId: this.selectedAgentBotId,
          functionId: this.selectedFunctionId,
          scheduleEnabled: this.scheduleEnabled,
          schedule: transformBotSchedule(this.scheduleSlots),
        });
        const data = await this.$store.dispatch(
          'agentBots/fetchAgentBotInbox',
          this.currentInboxId
        );
        this.applyScheduleFromResponse(data);
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.ERROR_MESSAGE'));
      }
    },
    async disconnectBot() {
      try {
        await this.$store.dispatch('agentBots/disconnectBot', {
          inboxId: this.inbox.id,
        });
        this.selectedFunctionId = null;
        this.functions = [];
        this.scheduleEnabled = false;
        this.scheduleSlots = defaultBotScheduleSlots();
        this.effectivelyActive = null;
        useAlert(
          this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          error?.message ||
            this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div class="mx-6 max-w-4xl flex flex-col gap-6">
    <LoadingState v-if="uiFlags.isFetching || uiFlags.isFetchingAgentBot" />
    <form
      v-else
      class="flex flex-col gap-6"
      @submit.prevent="updateActiveAgentBot"
    >
      <SettingsFieldSection
        :label="$t('AGENT_BOTS.BOT_CONFIGURATION.TITLE')"
        :help-text="$t('AGENT_BOTS.BOT_CONFIGURATION.DESC')"
        class="[&>div]:!items-start"
      >
        <div class="flex flex-col gap-4 w-full">
          <SelectInput
            v-model="selectedAgentBotId"
            :placeholder="$t('AGENT_BOTS.BOT_CONFIGURATION.SELECT_PLACEHOLDER')"
            :options="
              agentBots.map(bot => ({ value: bot.id, label: bot.name }))
            "
          />
          <div v-if="selectedAgentBotId" class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ $t('AGENT_BOTS.BOT_CONFIGURATION.FUNCTION_TITLE') }}
            </label>
            <p class="text-xs text-n-slate-11 mb-1">
              {{ $t('AGENT_BOTS.BOT_CONFIGURATION.FUNCTION_DESC') }}
            </p>
            <LoadingState v-if="isLoadingFunctions" />
            <SelectInput
              v-else
              v-model="selectedFunctionId"
              :placeholder="
                $t('AGENT_BOTS.BOT_CONFIGURATION.FUNCTION_PLACEHOLDER')
              "
              :options="functionOptions"
            />
            <p
              v-if="!isLoadingFunctions && !functionOptions.length"
              class="text-xs text-n-ruby-11"
            >
              {{ $t('AGENT_BOTS.BOT_CONFIGURATION.NO_FUNCTIONS') }}
            </p>
          </div>
        </div>
      </SettingsFieldSection>

      <SettingsToggleSection
        v-if="selectedAgentBotId"
        v-model="scheduleEnabled"
        :header="$t('AGENT_BOTS.BOT_CONFIGURATION.SCHEDULE.TITLE')"
        :description="$t('AGENT_BOTS.BOT_CONFIGURATION.SCHEDULE.DESC')"
      >
        <template #editor>
          <div class="flex flex-col gap-3 pt-2">
            <p class="text-xs text-n-slate-11">
              {{
                $t('AGENT_BOTS.BOT_CONFIGURATION.SCHEDULE.TIMEZONE', {
                  timezone: timezoneLabel,
                })
              }}
            </p>
            <p
              v-if="effectivelyActive !== null"
              class="text-xs"
              :class="effectivelyActive ? 'text-n-teal-11' : 'text-n-amber-11'"
            >
              {{
                effectivelyActive
                  ? $t('AGENT_BOTS.BOT_CONFIGURATION.SCHEDULE.STATUS_ACTIVE')
                  : $t('AGENT_BOTS.BOT_CONFIGURATION.SCHEDULE.STATUS_INACTIVE')
              }}
            </p>
            <div
              v-show="scheduleEnabled"
              class="overflow-x-auto outline outline-1 outline-n-weak rounded-xl"
            >
              <table class="w-full min-w-[640px]">
                <tbody>
                  <BotScheduleDay
                    v-for="slot in scheduleSlots"
                    :key="slot.day"
                    :day-name="dayNames[slot.day]"
                    :time-slot="slot"
                    @update="onScheduleDayUpdate"
                  />
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </SettingsToggleSection>

      <SettingsFieldSection
        v-if="selectedAgentBotId && activeAgentBot?.id"
        :label="$t('AGENT_BOTS.BOT_CONFIGURATION.EXCLUSIONS.TITLE')"
        :help-text="$t('AGENT_BOTS.BOT_CONFIGURATION.EXCLUSIONS.DESC')"
        class="[&>div]:!items-start"
      >
        <BotExclusionsList
          class="w-full"
          :inbox-id="currentInboxId"
          :enabled="Boolean(selectedAgentBotId && activeAgentBot?.id)"
        />
      </SettingsFieldSection>

      <div class="flex gap-2">
        <NextButton
          type="submit"
          :label="$t('AGENT_BOTS.BOT_CONFIGURATION.SUBMIT')"
          :is-loading="uiFlags.isSettingAgentBot"
          :disabled="!canSubmit"
        />
        <NextButton
          type="button"
          :disabled="!selectedAgentBotId"
          :is-loading="uiFlags.isDisconnecting"
          faded
          ruby
          @click="disconnectBot"
        >
          {{ $t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECT') }}
        </NextButton>
      </div>
    </form>
  </div>
</template>
